import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, HelpCircle, CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Clock, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import TurnstileWidget from "@/components/TurnstileWidget";

const CAPTCHA_ON = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Assessment({ embedded = false, onExit, prefill, slug: slugProp, resumeHandle }) {
  const [form, setForm] = useState(null);
  const [slug, setSlug] = useState(slugProp || "");
  const [stage, setStage] = useState(embedded ? "wizard" : "code");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [answers, setAnswers] = useState({});
  const [followups, setFollowups] = useState({});  // qId -> "pending" | "answered" (derived from fuMeta)
  const [fuMeta, setFuMeta] = useState({});  // qId -> { q, answer, skip } (persists across navigation)
  const fuGenerate = (qid, qText) => setFuMeta((m) => ({ ...m, [qid]: { q: qText, answer: "", skip: false } }));
  const fuAnswer = (qid, text) => setFuMeta((m) => ({ ...m, [qid]: { ...(m[qid] || { q: "", skip: false }), answer: text } }));
  const fuSkip = (qid, skip) => setFuMeta((m) => ({ ...m, [qid]: { ...(m[qid] || { q: "", answer: "" }), skip } }));

  // Gating: a generated follow-up blocks "Next" until it's answered or marked "Not applicable".
  useEffect(() => {
    const g = {};
    for (const [qid, fu] of Object.entries(fuMeta)) {
      if (fu?.q) g[qid] = (fu.skip || (fu.answer || "").trim()) ? "answered" : "pending";
    }
    setFollowups(g);
  }, [fuMeta]);
  const [stepIdx, setStepIdx] = useState(0);
  const [contact, setContact] = useState({
    client_name: prefill?.client || "", company: prefill?.company || "", client_email: prefill?.email || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [params] = useSearchParams();
  const [autoTried, setAutoTried] = useState(false);
  const [savingLater, setSavingLater] = useState(false);
  const [staffHandle, setStaffHandle] = useState("");
  const staffHandleRef = useRef("");   // authoritative handle to avoid duplicate rows during rapid auto-saves
  const staffSavingRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => { staffHandleRef.current = staffHandle; }, [staffHandle]);
  const [resumed, setResumed] = useState(false);
  const [verifyToken, setVerifyToken] = useState("");
  const [submitToken, setSubmitToken] = useState("");

  const resumeProgress = async (codeVal, emailVal) => {
    try {
      const em = emailVal || params.get("email") || contact.client_email || "";
      const { data } = await api.get(`/assessment/progress?code=${encodeURIComponent(codeVal)}&email=${encodeURIComponent(em)}`);
      if (data.found) {
        if (data.answers && Object.keys(data.answers).length) setAnswers(data.answers);
        if (data.followups && Object.keys(data.followups).length) setFuMeta(data.followups);
        setContact((c) => ({ ...c, client_name: data.client_name || c.client_name, company: data.company || c.company, client_email: data.email || c.client_email }));
        if (typeof data.step === "number") setStepIdx(data.step);
        toast.success("Welcome back — we've restored your progress.");
      }
    } catch (e) { /* resume is best-effort; ignore failures */ }
  };

  const loadForm = async (s) => {
    const { data } = await api.get(`/assessment/form?slug=${encodeURIComponent(s || "fsa")}`);
    setForm(data);
    return data;
  };

  // Pre-fill the "client data" questions (name / company / email) inside the wizard from a
  // selected client, so staff don't retype details they already have. Only fills blanks.
  const prefillClientAnswers = (loadedForm) => {
    if (!prefill || !loadedForm?.sections) return;
    const qs = loadedForm.sections.flatMap((s) => s.questions || []);
    const isText = (q) => ["text", "textarea"].includes(q.type);
    const nameQ = qs.find((q) => isText(q) && /your name|full name|founder name|\bname\?/i.test(q.title) && !/company|business|organi[sz]ation|role/i.test(q.title));
    const coQ = qs.find((q) => isText(q) && /company|business|organi[sz]ation/i.test(q.title));
    const emailQ = qs.find((q) => q.type === "email" || /e-?mail/i.test(q.title));
    setAnswers((a) => {
      const n = { ...a };
      if (nameQ && prefill.client && !n[nameQ.id]) n[nameQ.id] = prefill.client;
      if (coQ && prefill.company && !n[coQ.id]) n[coQ.id] = prefill.company;
      if (emailQ && prefill.email && !n[emailQ.id]) n[emailQ.id] = prefill.email;
      return n;
    });
  };

  useEffect(() => {
    if (embedded && slugProp) {
      setSlug(slugProp);
      loadForm(slugProp).then((f) => { if (!resumeHandle) prefillClientAnswers(f); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, slugProp]);

  // Staff resume: restore a saved in-progress session (from the admin "Open assessments" card).
  useEffect(() => {
    if (!embedded || !resumeHandle || resumed) return;
    setResumed(true);
    (async () => {
      try {
        const { data } = await api.get(`/assessments/in-progress/${encodeURIComponent(resumeHandle)}`);
        if (data.found) {
          if (!mounted.current) return;
          setStaffHandle(data.handle);
          setSlug(data.slug); await loadForm(data.slug);
          if (data.answers && Object.keys(data.answers).length) setAnswers(data.answers);
          if (data.followups && Object.keys(data.followups).length) setFuMeta(data.followups);
          setContact((c) => ({ ...c, client_name: data.client_name || c.client_name, company: data.company || c.company, client_email: data.email || c.client_email }));
          if (typeof data.step === "number") setStepIdx(data.step);
          toast.success("Resumed the saved session.");
        }
      } catch (e) { /* best-effort resume */ }
    })();
  }, [embedded, resumeHandle, resumed]);

  // Auto-start when arriving from an email link with ?code=...&email=... prefilled.
  useEffect(() => {
    if (embedded) return;
    const c = params.get("code");
    if (autoTried || stage !== "code" || !c) return;
    setAutoTried(true);
    setCode(c.toUpperCase());
    const em = params.get("email");
    if (em) setContact((x) => ({ ...x, client_email: em }));
    (async () => {
      setVerifying(true);
      try {
        const { data } = await api.post("/assessment/verify-code", { code: c.toUpperCase() });
        setSlug(data.slug); await loadForm(data.slug);
        setStage("wizard"); setStepIdx(0); await resumeProgress(c.toUpperCase());
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setVerifying(false); }
    })();
  }, [stage, autoTried, params, embedded]);

  // Auto-save progress (debounced) while a client takes the assessment.
  useEffect(() => {
    if (embedded || stage !== "wizard" || !code || !Object.keys(answers).length) return;
    const t = setTimeout(() => {
      api.post("/assessment/save-progress", { code, email: contact.client_email,
        client_name: contact.client_name, company: contact.company, answers, followups: fuMeta, step: stepIdx }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [answers, fuMeta, stepIdx, stage]);

  // Save a live/staff session. Guarded so rapid auto-saves can't create duplicate rows
  // before the first save returns a handle. Reused by the button and the auto-save effect.
  const doStaffSave = async (email) => {
    if (!staffHandleRef.current && staffSavingRef.current) return null;  // avoid duplicate creation
    staffSavingRef.current = true;
    try {
      const { data } = await api.post("/assessments/staff-save-progress", {
        handle: staffHandleRef.current || undefined, slug, email,
        client_name: contact.client_name, company: contact.company,
        answers, followups: fuMeta, step: stepIdx });
      if (data.handle) { staffHandleRef.current = data.handle; if (mounted.current) setStaffHandle(data.handle); }
      return data.handle;
    } finally { staffSavingRef.current = false; }
  };

  // Auto-save live/staff sessions once a client email is known, so nothing is lost if the tab closes.
  useEffect(() => {
    if (!embedded || stage !== "wizard") return;
    const email = (contact.client_email || "").trim();
    if (!email || !Object.keys(answers).length) return;
    const t = setTimeout(() => { doStaffSave(email).catch(() => {}); }, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, stage, answers, fuMeta, stepIdx, contact.client_email, contact.client_name, contact.company]);

  // Visible section sequence: disclaimer + always sections (no branching).
  const seq = useMemo(() => {
    if (!form) return [];
    const out = [];
    out.push(...form.sections.filter((s) => s.role === "disclaimer"));
    out.push(...form.sections.filter((s) => s.role === "always"));
    return out;
  }, [form]);

  const declined = useMemo(() => {
    if (!form) return false;
    const d = form.sections.find((s) => s.role === "disclaimer");
    const q = d?.questions[0];
    return q && answers[q.id] && answers[q.id].startsWith("I do not");
  }, [form, answers]);

  if (!form && stage !== "code") return <div className={`${embedded ? "" : "min-h-screen"} grid place-items-center bg-upland-green text-upland-creme py-20`}>Loading…</div>;

  const setAns = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/assessment/verify-code", { code, turnstile_token: verifyToken });
      setSlug(data.slug); await loadForm(data.slug);
      setStage("wizard"); setStepIdx(0); await resumeProgress(code.toUpperCase());
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setVerifying(false); }
  };

  const saveForLater = async () => {
    const email = contact.client_email || window.prompt(embedded
      ? "Enter the client's email to save this session so it can be resumed later:"
      : "Enter your email and we'll send you a link to resume this assessment later:");
    if (!email) return;
    setContact((c) => ({ ...c, client_email: email }));
    setSavingLater(true);
    try {
      if (embedded) {
        await doStaffSave(email);
        toast.success("Session saved — find it under Open assessments to resume.");
      } else {
        await api.post("/assessment/save-progress", { code, email, client_name: contact.client_name,
          company: contact.company, answers, followups: fuMeta, step: stepIdx, email_link: true });
        toast.success("Progress saved — check your email for a link to continue later.");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingLater(false); }
  };

  const section = seq[stepIdx];
  const isBlank = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
  const missingRequired = section ? section.questions.filter((q) => q.required && isBlank(answers[q.id])) : [];
  const pendingFollowups = section ? section.questions.filter((q) => followups[q.id] === "pending") : [];

  const prefillContact = () => {
    const nameQ = form.sections.flatMap((s) => s.questions).find((q) => q.title === "What is your name?");
    const coQ = form.sections.flatMap((s) => s.questions).find((q) => q.title.startsWith("What is the name of your company"));
    setContact((c) => ({ ...c, client_name: answers[nameQ?.id] || c.client_name, company: answers[coQ?.id] || c.company }));
  };

  const next = () => {
    if (declined) return;
    if (missingRequired.length) { toast.error("Please answer the required question(s)."); return; }
    if (pendingFollowups.length) { toast.error("Please answer the follow-up question(s) before continuing."); return; }
    if (stepIdx + 1 < seq.length) setStepIdx(stepIdx + 1);
    else { prefillContact(); setStage("contact"); }
  };
  const back = () => { if (stepIdx > 0) setStepIdx(stepIdx - 1); else if (!embedded) setStage("code"); };

  const buildAnswers = () => seq.flatMap((s) => s.questions.map((q) => {
    let answer = Array.isArray(answers[q.id]) ? answers[q.id].join(", ") : (answers[q.id] || "");
    const fu = fuMeta[q.id];
    if (fu?.q && (fu.skip || (fu.answer || "").trim())) {
      const base = answer ? `${answer}` : "";
      answer = `${base}\n\n[Follow-up] ${fu.q} ${fu.skip ? "(Not applicable)" : fu.answer.trim()}`.trim();
    }
    return { section: s.title || "General", question: q.title, answer };
  })).filter((a) => a.answer);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payloadAnswers = buildAnswers();
      if (embedded) {
        await api.post("/assessments/staff-run", { slug, ...contact, answers: payloadAnswers, handle: staffHandleRef.current || undefined });
      } else {
        await api.post("/assessment/submit", { code, ...contact, answers: payloadAnswers, turnstile_token: submitToken });
      }
      setStage("done");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full border border-upland-brown/40 bg-white px-3 py-2.5";
  const recap = buildAnswers();

  const body = (
    <div className={`${embedded ? "" : "max-w-3xl mx-auto"} px-0`}>
      {!embedded && (
        <div className="flex items-center justify-between gap-3 mb-10">
          <a href="https://uplandgroupllc.com" data-testid="assessment-logo-link" className="flex items-center gap-3 group">
            <span className="h-10 w-10 grid place-items-center bg-upland-creme rounded-sm p-1.5">
              <img src="/upland-logo.png" alt="Upland Group" className="h-full w-auto object-contain" />
            </span>
            <span className="font-serif text-2xl">Upland Group</span>
          </a>
          <a href="https://uplandgroupllc.com" data-testid="assessment-back-to-site" className="flex items-center gap-2 text-sm text-upland-creme/80 hover:text-upland-creme transition-colors">
            <ArrowLeft size={16} /> Back to website
          </a>
        </div>
      )}

      {embedded && stage !== "done" && (
        <div className="flex items-center justify-between gap-3 mb-6">
          <p className="text-sm text-upland-creme/70" data-testid="embedded-run-note">Running live · {contact.company || "New founder session"}</p>
          <button onClick={onExit} data-testid="embedded-exit" className="flex items-center gap-2 text-sm text-upland-creme/80 hover:text-upland-creme">
            <X size={16} /> Exit to dashboard
          </button>
        </div>
      )}

      {stage === "code" && (
        <div className="bg-upland-offwhite text-upland-green p-10 border border-upland-brown/20" data-testid="assessment-code-stage">
          <h1 className="font-serif text-4xl mb-3">Upland Group Assessment</h1>
          <p className="text-upland-grey mb-2">Welcome. Your Upland Group contact has invited you to complete a guided assessment.</p>
          <p className="text-upland-grey mb-6 text-sm">Enter the access code provided by your Upland Group contact to begin.</p>
          <div className="flex gap-2 max-w-md">
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="assessment-code-input"
              placeholder="ACCESS CODE" className="flex-1 border border-upland-brown/40 bg-white px-4 py-3 tracking-widest font-mono focus:outline-none focus:border-upland-green" />
            <button onClick={verify} disabled={verifying || !code || (CAPTCHA_ON && !verifyToken)} data-testid="verify-code-btn"
              className="bg-upland-green text-upland-creme px-6 py-3 hover:bg-upland-brown transition-colors disabled:opacity-50 flex items-center gap-2">
              {verifying ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />} Begin
            </button>
          </div>
          <TurnstileWidget className="mt-5" onVerify={setVerifyToken} onExpire={() => setVerifyToken("")} action="verify-code" />
        </div>
      )}

      {stage === "wizard" && section && (
        <div data-testid="assessment-wizard">
          <div className={`h-1 mb-8 rounded-full overflow-hidden ${embedded ? "bg-upland-brown/20" : "bg-upland-creme/20"}`}>
            <div className="h-full bg-upland-creme transition-all" style={{ width: `${((stepIdx + 1) / (seq.length + 1)) * 100}%`, backgroundColor: embedded ? "#184029" : undefined }} />
          </div>
          <div className="bg-upland-offwhite text-upland-green p-8 md:p-10 border border-upland-brown/20">
            {section.title && <h2 className="font-serif text-3xl mb-1">{section.title}</h2>}
            {section.description && <p className="text-upland-grey mb-6 text-sm leading-relaxed">{section.description}</p>}
            {declined && (
              <p className="text-red-700 my-4" data-testid="assessment-declined">You must accept the disclaimer to continue. If you have questions, please contact inquiries@uplandgroupllc.com.</p>
            )}
            <div className="space-y-8 mt-4">
              {section.questions.map((q) => (
                <Question key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAns(q.id, v)}
                  fu={fuMeta[q.id]} onFuGenerate={fuGenerate} onFuAnswer={fuAnswer} onFuSkip={fuSkip} />
              ))}
            </div>
            <div className="flex justify-between items-center mt-10 gap-3 flex-wrap">
              <button onClick={back} data-testid="wizard-back" className="flex items-center gap-2 text-upland-green border border-upland-brown/40 px-5 py-2.5 hover:bg-upland-creme">
                <ArrowLeft size={16} /> Back
              </button>
              <div className="flex items-center gap-3">
                <button onClick={saveForLater} disabled={savingLater} data-testid="save-for-later" className="flex items-center gap-2 text-upland-brown text-sm hover:text-upland-green transition-colors disabled:opacity-50">
                  {savingLater ? <Loader2 className="animate-spin" size={15} /> : <Clock size={15} />} Save &amp; continue later
                </button>
                {pendingFollowups.length > 0 && (
                  <span className="text-xs text-upland-brown" data-testid="followup-blocking-hint">Please answer the follow-up to continue</span>
                )}
                <button onClick={next} disabled={declined || pendingFollowups.length > 0} data-testid="wizard-next" className="flex items-center gap-2 bg-upland-green text-upland-creme px-7 py-2.5 hover:bg-upland-brown transition-colors disabled:opacity-50">
                  {stepIdx + 1 < seq.length ? "Next" : "Review & finish"} <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {stage === "contact" && (
        <div className="bg-upland-offwhite text-upland-green p-10 border border-upland-brown/20" data-testid="assessment-contact-stage">
          <h2 className="font-serif text-3xl mb-2">Almost done</h2>
          <p className="text-upland-grey mb-6 text-sm">{embedded ? "Confirm the founder's details for this Design Brief." : "Confirm your details so our team can prepare your Design Brief."}</p>
          <div className="space-y-3 max-w-md">
            <input value={contact.client_name} onChange={(e) => setContact({ ...contact, client_name: e.target.value })} placeholder="Founder name" data-testid="contact-name" className={inputCls} />
            <input value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} placeholder="Company" data-testid="contact-company" className={inputCls} />
            <input value={contact.client_email} onChange={(e) => setContact({ ...contact, client_email: e.target.value })} placeholder="Email" type="email" data-testid="contact-email" className={inputCls} />
          </div>
          {!embedded && (
            <div className="mt-6"><TurnstileWidget onVerify={setSubmitToken} onExpire={() => setSubmitToken("")} action="submit-assessment" /></div>
          )}
          <div className="flex justify-between mt-8">
            <button onClick={() => setStage("wizard")} className="flex items-center gap-2 text-upland-green border border-upland-brown/40 px-5 py-2.5 hover:bg-upland-creme"><ArrowLeft size={16} /> Back</button>
            <button onClick={submit} disabled={submitting || (!embedded && CAPTCHA_ON && !submitToken)} data-testid="submit-assessment-btn" className="flex items-center gap-2 bg-upland-green text-upland-creme px-7 py-2.5 hover:bg-upland-brown transition-colors disabled:opacity-50">
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />} Submit assessment
            </button>
          </div>
        </div>
      )}

      {stage === "done" && (
        <div className="bg-upland-offwhite text-upland-green p-10 md:p-12 border border-upland-brown/20" data-testid="assessment-done">
          <div className="text-center">
            <CheckCircle2 size={56} className="mx-auto text-upland-green" />
            <h2 className="font-serif text-4xl mt-5">Thank you.</h2>
            <p className="text-upland-grey mt-3 max-w-lg mx-auto">
              {embedded
                ? "The assessment has been recorded and the Plain English Design Brief has been generated and emailed to the Upland team."
                : "Your responses have been received. Our team will review them and prepare your Plain English Design Brief. A copy of your entries is below for your records."}
            </p>
          </div>

          <div className="mt-10 border-t border-upland-brown/20 pt-8" data-testid="assessment-recap">
            <h3 className="font-serif text-2xl mb-4">Your responses</h3>
            <div className="space-y-5">
              {recap.map((a, i) => (
                <div key={`${a.section}-${a.question}-${i}`} className="border-l-2 border-upland-brown/30 pl-4">
                  <p className="text-[11px] uppercase tracking-wide text-upland-grey">{a.section}</p>
                  <p className="font-medium text-upland-green text-sm mt-0.5">{a.question}</p>
                  <p className="text-upland-green/80 text-sm mt-1 whitespace-pre-wrap">{a.answer}</p>
                </div>
              ))}
            </div>
          </div>

          {embedded && (
            <div className="mt-8 text-center">
              <button onClick={onExit} data-testid="done-exit" className="bg-upland-green text-upland-creme px-7 py-2.5 hover:bg-upland-brown transition-colors">Back to dashboard</button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (embedded) return <div data-testid="embedded-assessment">{body}</div>;
  return (
    <div className="min-h-screen bg-upland-green text-upland-creme font-sans">
      <div className="max-w-3xl mx-auto px-6 py-12">{body}</div>
    </div>
  );
}

function Question({ q, value, onChange, fu, onFuGenerate, onFuAnswer, onFuSkip }) {
  const [help, setHelp] = useState(null);
  const [loadingHelp, setLoadingHelp] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const cls = "w-full border border-upland-brown/40 bg-white px-3 py-2.5 focus:outline-none focus:border-upland-green";

  const clarify = async () => {
    setLoadingHelp(true);
    try {
      const { data } = await api.post("/assessment/clarify", { question: q.title, options: (q.options || []).join(" / ") });
      setHelp(data.text);
    } catch (e) { toast.error("The assistant is unavailable right now."); }
    finally { setLoadingHelp(false); }
  };

  const checkFollowup = async (val) => {
    if (q.type !== "textarea" || !val || val.trim().length < 8) return;
    if (fu?.q) return;  // already generated — never auto-change it (persists across navigation)
    try {
      const { data } = await api.post("/assessment/followup", { question: q.title, answer: val });
      if (data.followup) onFuGenerate(q.id, data.followup);
    } catch (e) { /* follow-up suggestion is non-blocking */ }
  };

  const askDifferent = async () => {
    setRegenerating(true);
    try {
      const base = (value || "").toString();
      const { data } = await api.post("/assessment/followup", { question: q.title, answer: base, avoid: fu?.q || "" });
      if (data.followup) onFuGenerate(q.id, data.followup);
      else toast.info("Your answer looks clear — no different follow-up needed.");
    } catch (e) { toast.error("The assistant is unavailable right now."); }
    finally { setRegenerating(false); }
  };

  return (
    <div data-testid={`q-${q.id}`}>
      <div className="flex items-start gap-2">
        <label className="block font-medium text-upland-green flex-1">{q.title}{q.required && <span className="text-red-600"> *</span>}</label>
        <button type="button" onClick={clarify} data-testid={`clarify-${q.id}`} title="What does this mean?" className="text-upland-brown hover:text-upland-green shrink-0 mt-0.5">
          {loadingHelp ? <Loader2 size={16} className="animate-spin" /> : <HelpCircle size={16} />}
        </button>
      </div>
      {q.description && <p className="text-xs text-upland-grey mt-1">{q.description}</p>}
      {help && (
        <div className="mt-2 bg-upland-creme border-l-2 border-upland-brown px-3 py-2 text-sm text-upland-green/90 flex gap-2" data-testid={`help-${q.id}`}>
          <Sparkles size={15} className="text-upland-brown shrink-0 mt-0.5" /> <span>{help}</span>
        </div>
      )}

      <div className="mt-3">
        {q.type === "text" && <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} />}
        {q.type === "textarea" && <textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={(e) => checkFollowup(e.target.value)} className={cls} />}
        {q.type === "select" && (
          <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls}>
            <option value="">Select…</option>
            {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        {q.type === "radio" && (
          <div className="space-y-2">
            {(q.options || []).map((o) => (
              <label key={o} className="flex items-center gap-3 cursor-pointer text-upland-green/90">
                <input type="radio" name={q.id} checked={value === o} onChange={() => onChange(o)} className="accent-upland-green" /> {o}
              </label>
            ))}
            {value && (
              <button type="button" onClick={() => onChange("")} data-testid={`radio-reset-${q.id}`}
                className="mt-1 text-xs text-upland-brown hover:text-upland-green underline underline-offset-2">
                Reset responses
              </button>
            )}
          </div>
        )}
        {q.type === "checkbox" && (
          <div className="space-y-2">
            {(q.options || []).map((o) => {
              const arr = value || [];
              const on = arr.includes(o);
              return (
                <label key={o} className="flex items-center gap-3 cursor-pointer text-upland-green/90">
                  <input type="checkbox" checked={on} onChange={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])} className="accent-upland-green" /> {o}
                </label>
              );
            })}
          </div>
        )}
        {q.type === "number" && (
          <input type="number" value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`q-number-${q.id}`} />
        )}
        {q.type === "date" && (
          <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`q-date-${q.id}`} />
        )}
        {q.type === "rating" && (
          <div className="flex gap-2" data-testid={`q-rating-${q.id}`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} onClick={() => onChange(String(n))}
                className={`h-10 w-10 border text-sm font-medium transition-colors ${
                  String(value) === String(n) ? "bg-upland-green text-upland-creme border-upland-green" : "border-upland-brown/40 text-upland-green hover:bg-upland-creme"
                }`}>{n}</button>
            ))}
          </div>
        )}
      </div>

      {fu?.q && (
        <div className="mt-3 border-l-2 border-upland-brown pl-3" data-testid={`followup-${q.id}`}>
          <p className="text-sm text-upland-brown flex items-center gap-1 mb-1">
            <Sparkles size={14} /> {fu.q}<span className="text-red-600"> *</span>
          </p>
          <textarea rows={2} value={fu.answer || ""} placeholder="Please respond before continuing"
            data-testid={`followup-input-${q.id}`} disabled={fu.skip}
            onChange={(e) => onFuAnswer(q.id, e.target.value)} className={cls + (fu.skip ? " opacity-50" : "")} />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => onFuSkip(q.id, !fu.skip)} data-testid={`followup-na-${q.id}`}
              className={`text-xs px-3 py-1.5 border transition-colors ${fu.skip ? "bg-upland-green text-upland-creme border-upland-green" : "border-upland-brown/40 text-upland-green hover:bg-upland-creme"}`}>
              Not applicable
            </button>
            <button type="button" onClick={askDifferent} disabled={regenerating} data-testid={`followup-different-${q.id}`}
              className="text-xs px-3 py-1.5 border border-upland-brown/40 text-upland-green hover:bg-upland-creme transition-colors disabled:opacity-50 flex items-center gap-1.5">
              {regenerating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Ask me a different follow-up
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
